import axios from 'axios';

export const frontEndBaseURL = process.env.REACT_APP_FRONTEND_URL;

// Token storage keys are per-environment so dev/staging/prod don't share tokens
function getTokenStorageKey(name) {
  const base = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location?.origin : '') || '';
  const envId = (base || 'default').replace(/[^a-zA-Z0-9]/g, '_').slice(0, 48) || 'app';
  return envId ? `${name}_${envId}` : name;
}

export const getStoredAccessToken = () => {
  const key = getTokenStorageKey('accessToken');
  let v = localStorage.getItem(key) || '';
  if (!v && key !== 'accessToken') {
    v = localStorage.getItem('accessToken') || '';
    if (v) {
      localStorage.setItem(key, v);
      localStorage.removeItem('accessToken');
    }
  }
  return v;
};
export const setStoredAccessToken = token => token && localStorage.setItem(getTokenStorageKey('accessToken'), token);
export const getStoredRefreshToken = () => {
  const key = getTokenStorageKey('refreshToken');
  let v = localStorage.getItem(key) || '';
  if (!v && key !== 'refreshToken') {
    v = localStorage.getItem('refreshToken') || '';
    if (v) {
      localStorage.setItem(key, v);
      localStorage.removeItem('refreshToken');
    }
  }
  return v;
};
export const setStoredRefreshToken = token => token && localStorage.setItem(getTokenStorageKey('refreshToken'), token);
export const removeStoredTokens = () => {
  localStorage.removeItem(getTokenStorageKey('accessToken'));
  localStorage.removeItem(getTokenStorageKey('refreshToken'));
};

export const API_public = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true, // this allows cookies to be set for res.cookie(...), AND for Frontend to send that cookie as well.
});

API_public.interceptors.response.use(
  response => {
    return response; // Continue with the response
  },
  error => {
    console.log(error?.message);
    if (error.response?.status === 429) {
      alert('Too many requests, please try again later.');
      return Promise.resolve();
    }
    return Promise.reject(error); // For other errors, reject the promise
  }
);

export const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
});

// Request interceptor to add access token
API.interceptors.request.use(
  config => {
    const token = getAccessToken();
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor with token refresh retry mechanism
API.interceptors.response.use(
  response => {
    return response; // Continue with the response
  },
  async error => {
    const originalRequest = error.config;

    console.info('Error intercepted', error?.message);
    console.log('Request URL:', originalRequest?.url);
    console.log('Request Method:', originalRequest?.method);

    // Handle refresh token endpoint errors separately (url may be relative or include base)
    if (originalRequest?.url?.includes?.('refresh-token')) {
      // Refresh token failed - clear tokens and redirect to login
      isRefreshing = false;
      processQueue(null, REDIRECTING_TO_LOGIN);
      removeStoredTokens();
      setGlobalAccessToken('');
      logoutUser().then(() => {
        window.location.href = '/login';
      });
      // Don't reject - redirect is in progress; rejecting causes uncaught 401 overlay
      return new Promise(() => {});
    }

    // Handle 401 Unauthorized - try to refresh token
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            if (token === REDIRECTING_TO_LOGIN) return new Promise(() => {}); // Redirect in progress, don't reject
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return API(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

        try {
          const response = await getNewAccessAndRefreshToken();
          if (response?.data?.accessToken) {
            const newToken = response.data.accessToken;
            const newRefreshToken = response.data.refreshToken;
            setGlobalAccessToken(newToken);
            setStoredAccessToken(newToken);
            if (newRefreshToken) setStoredRefreshToken(newRefreshToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            processQueue(null, newToken);
            isRefreshing = false;
            return API(originalRequest);
          } else {
            throw new Error('No access token in refresh response');
          }
        } catch (refreshError) {
        // Refresh token failed - clear tokens and redirect to login
        isRefreshing = false;
        processQueue(null, REDIRECTING_TO_LOGIN); // Don't reject queue - would cause uncaught 401 in useQuery etc
        removeStoredTokens();
        setGlobalAccessToken('');
        logoutUser().then(() => {
          window.location.href = '/login';
        });
        // Don't reject - redirect is in progress; rejecting causes uncaught 401 overlay
        return new Promise(() => {});
      }
    }

    // Handle 403 Forbidden - check if it's a token issue or permission issue
    if (error.response?.status === 403) {
      // Only logout if it's a token-related 403, not a permission issue
      // If the request was retried and still 403, it's likely a permission issue, not token
      if (originalRequest?._retry) {
        // Permission denied, don't logout
        return Promise.reject(error);
      }
      // First 403 - try refreshing token once
      if (originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const response = await getNewAccessAndRefreshToken();
          if (response?.data?.accessToken) {
            const newToken = response.data.accessToken;
            const newRefreshToken = response.data.refreshToken;
            setGlobalAccessToken(newToken);
            setStoredAccessToken(newToken);
            if (newRefreshToken) setStoredRefreshToken(newRefreshToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            processQueue(null, newToken);
            isRefreshing = false;
            return API(originalRequest);
          }
        } catch (refreshError) {
          // Refresh failed, but 403 might be permission issue, not token issue
          // Don't logout for 403 unless we're sure it's token-related
          isRefreshing = false;
          processQueue(refreshError, null);
        }
      }
      // Permission denied or refresh failed, reject the request
      return Promise.reject(error);
    }

    // Handle other error statuses
    if (error.response?.status === 429) {
      alert('Too many requests, please try again later.');
      return Promise.reject(new Error('Too many requests, try again later.'));
    } else if (error.response?.status === 400) {
      alert('Allowed Input length exceeded! Please enter a shorter input or contact the Admin.');
      return Promise.reject(new Error('Input length exceeded.'));
    }

    return Promise.reject(error);
  }
);

// Global variable to store access token
let globalAccessToken = '';
let isRefreshing = false;
let failedQueue = [];

// When redirecting to login, resolve queued requests with this so they don't reject (avoids uncaught 401)
const REDIRECTING_TO_LOGIN = Symbol('REDIRECTING_TO_LOGIN');

// Function to set the global access token
export const setGlobalAccessToken = token => {
  globalAccessToken = token || '';
};

function getAccessToken() {
  // Try to get from global variable first
  if (globalAccessToken) {
    return globalAccessToken;
  }
  // Fallback to localStorage (for backward compatibility)
  return getStoredAccessToken();
} // jwt stored in res.cookie from backend, but we still need it in Authorization header

// Function to process failed queue after token refresh
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// ---------------- login/reset password ------------------------
export const loginUser = obj => API_public.post(`/user/login`, obj);
export const logoutUser = () => API_public.post(`/user/logout`);
// Get refreshToken from localStorage and pass via Authorization header
export const getNewAccessAndRefreshToken = () => {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token available'));
  }
  return API.post(`/user/refresh-token`, null, {
    headers: { Authorization: `Bearer ${refreshToken}` },
  });
};

export const updateUserOtp = obj => API_public.put(`/user/otp`, obj);
export const verifyOtp = obj => API_public.post(`/user/verify-otp`, obj);
export const updateForgetPassword = obj => API_public.put(`/user/reset-password`, obj);
export const updatePassword = (obj, token) =>
  API.put(`/user/update-password`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });

// ---------------- Trusted Devices ------------------------
export const getTrustedDevices = () =>
  API.get(`/user/trusted-devices`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const revokeTrustedDevice = deviceId =>
  API.delete(`/user/trusted-devices/${deviceId}`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const revokeAllTrustedDevices = () =>
  API.delete(`/user/trusted-devices`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });

// ---------------- Admin ------------------------
export const getAllUsers = token => API.get(`/user/all`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const createUser = (obj, token) =>
  API.post(`/user/create-user`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const updateUser = (obj, token) =>
  API.put(`/user/update-user`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const deleteUser = (id, token) =>
  API.delete(`/user/delete-user?id=${id}`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });

// ---------------- Profile ------------------------
export const updateCompanyDetails = (obj, token) =>
  API.put(`/user/update-company`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } }); // have to manually create for the first time
export const getUserById = (id, token) =>
  API.get(`/user/getUserById?id=${id}`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const getCompanyDetails = (type, token) =>
  API.get(`/user/company?type=${type}`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });

// ---------------- SuperAdmin ------------------------
export const getAllCompanyAndUsers = token =>
  API.get(`/superadmin/company`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const createCompany = (obj, token) =>
  API.post(`/superadmin/company`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const updateCompany = (companyId, token) =>
  API.put(`/superadmin/company?companyId=${companyId}`, null, {
    headers: { Authorization: `Bearer ${getAccessToken()}` },
  });
export const deleteCompany = (companyId, token) =>
  API.delete(`/superadmin/kyc-screening-interval`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });

export const getAllCompanyKycDetails = token =>
  API.get(`/superadmin/kyc-billing`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const updateCompanyKycDetails = (companyId, token) =>
  API.put(`/superadmin/kyc-billing?companyId=${companyId}`, null, {
    headers: { Authorization: `Bearer ${getAccessToken()}` },
  });

// ---------------- KYC-AML module ------------------------
export const getkyc = (clientFormId, formToken) =>
  API.get(`/kyc/client?clientFormId=${clientFormId}&formToken=${formToken}`);
/**
 * 获取所有 KYC 客户数据
 * @param {string} companyId - 公司ID
 * @param {string} token - 令牌（可选）
 * @param {number} page - 页码（可选）
 * @param {number} pageSize - 每页数量（可选）
 * @param {boolean} archived - 是否只获取已归档数据（可选，true/false）
 * @param {object} filters - 筛选和搜索参数（可选）
 * @param {string} filters.search - 搜索关键词
 * @param {string} filters.sortBy - 排序字段
 * @param {string} filters.sortOrder - 排序方向：'asc' | 'desc'
 * @param {string} filters.status - 状态筛选
 * @param {string} filters.riskLevel - 风险等级筛选
 * @param {string} filters.type - 类型筛选
 * @param {string} filters.formAuthority - 表单权限筛选
 */
export const getkycAll = (companyId, token, page, pageSize, archived, filters = {}) => {
  let url = `/kyc/client/all?companyId=${companyId}`;
  if (page !== undefined && pageSize !== undefined) {
    url += `&page=${page}&pageSize=${pageSize}`;
  }
  if (archived !== undefined) {
    url += `&archived=${archived}`;
  }

  // 添加搜索参数
  if (filters.search) {
    url += `&search=${encodeURIComponent(filters.search)}`;
  }

  // 添加排序参数
  if (filters.sortBy) {
    url += `&sortBy=${encodeURIComponent(filters.sortBy)}`;
  }

  if (filters.sortOrder) {
    url += `&sortOrder=${encodeURIComponent(filters.sortOrder)}`;
  }

  // 添加筛选参数
  if (filters.status) {
    url += `&status=${encodeURIComponent(filters.status)}`;
  }

  if (filters.riskLevel) {
    url += `&riskLevel=${encodeURIComponent(filters.riskLevel)}`;
  }

  if (filters.type) {
    url += `&type=${encodeURIComponent(filters.type)}`;
  }

  if (filters.formAuthority) {
    url += `&formAuthority=${encodeURIComponent(filters.formAuthority)}`;
  }

  return API.get(url, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
};
export const getkycPDF = (userId, token) =>
  API.get(`/kyc/client/pdf?userId=${userId}`, {
    headers: { Authorization: `Bearer ${getAccessToken()}` },
    responseType: 'blob',
  });
export const getKycFormToCsv = (userId, token) =>
  API.get(`/kyc/client/csv?userId=${userId}`, {
    headers: { Authorization: `Bearer ${getAccessToken()}` },
    responseType: 'blob',
  });

export const createkyc = (obj, token) =>
  API.post(`/kyc/client`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const updatekyc = (data, token) =>
  API.put(`/kyc/client`, data, { headers: { Authorization: `Bearer ${token}` } }); // not getLocalStorage, because client form view has no localStorage
export const updatekycStatus = (data, token) =>
  API.put(`/kyc/client-status`, data, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const updateKycAmlVideo = (data, token) =>
  API.put(`/kyc/updateKycAmlVideo`, data, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const updateKycArchivedStatus = (data, token) =>
  API.put(`/kyc/updateKycArchivedStatus`, data, { headers: { Authorization: `Bearer ${getAccessToken()}` } });

export const deletekyc = (userId, token) =>
  API.delete(`/kyc/client/?userId=${userId}`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });

export const getKycClientRecords = (clientId, token) =>
  API.get(`/kyc/client/records?clientId=${clientId}`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });

export const getkycRiskAssessment = (userId, token) =>
  API.get(`/kyc/risk?userId=${userId}`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const getkycRiskAssessmentById = (riskAssessmentId, token) =>
  API.get(`/kyc/risk/id?riskAssessmentId=${riskAssessmentId}`, {
    headers: { Authorization: `Bearer ${getAccessToken()}` },
  });
export const getkycCustomerPastRisk = (customerId, token) =>
  API.get(`/kyc/risk/all?customerId=${customerId}`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
// above 2 is identical lol. return list of assessments??
export const getKycAllClientsRisks = token =>
  API.get(`/kyc/risk/all-clients`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });

export const createkycRiskAssessment = (obj, token) =>
  API.post(`/kyc/risk`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const updatekycRiskAssessment = (obj, token) =>
  API.put(`/kyc/risk`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const updatekycRiskById = (obj, token) =>
  API.put(`/kyc/riskById`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });

export const createKycComment = (obj, token) =>
  API.post(`/kyc/risk/comment`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const createKycNestedSanctionComment = (obj, token) =>
  API.post(`/kyc/risk/sanction_comment`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });

export const getKycBilling = token =>
  API.get(`/kyc/bill`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const updateKycBillingLog = obj =>
  API.post(`/kyc/bill`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const updateKycBillingAlert = obj =>
  API.put(`/kyc/bill`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const getRescreeningFrequency = token =>
  API.get(`/kyc/frequency`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const kycRiskReview = (obj, token) =>
  API.post(`/kyc/kycRiskReview`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });

export const sanctionIoHits = obj =>
  API.post(`/sanctions/hits`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const worldCheckHits = obj =>
  API.post(`/worldcheck/hits`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const amlWatcherHits = obj =>
  API.post(`/amlwatcher/hits`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });

export const createKycRecord = obj =>
  API.put('/kyc/record', obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const getKycRecord = kycaml_client_id =>
  API.get(`/kyc/record?kycaml_client_id=${kycaml_client_id}`, {
    headers: { Authorization: `Bearer ${getAccessToken()}` },
  });

// ---------------- CMP module ------------------------
export const getCmpTopic = (topicId, token) =>
  API.get(`/cmp/topic?topicId=${topicId}`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const getCmpAllTopics = token =>
  API.get(`/cmp/alltopics`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const createCmpTopic = (obj, token) =>
  API.post(`/cmp/topic`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const updateCmpTopic = (obj, topicId, token) =>
  API.put(`/cmp/topic?topicId=${topicId}`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const deleteCmpTopic = (topicId, token) =>
  API.delete(`/cmp/topic?topicId=${topicId}`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });

export const getCmpForm = (formId, token) =>
  API.get(`/cmp/form?formId=${formId}`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const getCmpAllForms = token =>
  API.get(`/cmp/allforms`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const createCmpForm = (obj, token) =>
  API.post(`/cmp/form`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const updateCmpForm = (obj, token) =>
  API.put(`/cmp/form`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const deleteCmpForm = (formId, token) =>
  API.delete(`/cmp/form?formId=${formId}`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const archiveCmpForm = (formId, token) =>
  API.delete(`/cmp/archive-form?formId=${formId}`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });

export const getCmpFormToCsv = (formId, token) =>
  API.get(`/cmp/form-to-csv?formId=${formId}`, {
    headers: { Authorization: `Bearer ${getAccessToken()}` },
    responseType: 'arraybuffer',
  });
export const getCmpFormToLogs = (formId, token) =>
  API.get(`/cmp/form-to-logs?formId=${formId}`, {
    headers: { Authorization: `Bearer ${getAccessToken()}` },
    responseType: 'arraybuffer',
  });

// ---------------- notifications ------------------------
export const getAllNotifications = (module, token) =>
  API.get(`/notification/getAll?moduleType=${module}`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const createNotification = (obj, token) =>
  API.post(`/notification/create`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const sendEmailNotification = obj => API.post(`/notification/email`, obj); // to auth only requests from FE, have to use CORS

// ---------------- shared ------------------------
export const generateFormToken = (tokenData, token) =>
  API.post(`/user/formtoken`, tokenData, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const generateShareLink = (obj, token) =>
  API.post(`/share/generate`, obj, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const getShareLink = (shortLink, token) =>
  API.get(`/share/short?short=${shortLink}`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
export const notifyEmail = (uuid, data, token) =>
  API.post(`/share/notify?_d=${uuid}`, data, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
// export const makeTinyUrl = (url) => API.get(`http://tinyurl.com/api-create.php?url=${url}`);
export const uploadFile = (obj, filename, token) =>
  API.post(`/upload?filename=${filename}&formToken=${token}`, obj, {
    headers: { Authorization: `Bearer ${getAccessToken()}` },
  });
// export const sendEmailNotification = (obj) => API.post(`/notification/send`, obj).then(() => {
// PNotify.success({
//   title: 'Notification sent'
//   }
// })

export const generatePDF = obj => API.post(`/generatePDF`, obj, { responseType: 'blob' });
export const generateXLSX = obj => API.post(`/generateXLSX`, obj, { responseType: 'blob' });
export const downloadXLSXTemplate = () => API.get(`/downloadXLSXTemplate`, { responseType: 'blob' });
export const downloadFPTemplate = () => API.get(`/downloadFPTemplate`, { responseType: 'blob' });
export const downloadKYCTemplate = () => API.get(`/downloadKYCTemplate`, { responseType: 'blob' });

// ---------------- API调用记录 ------------------------
export const getKycApiCallRecords = params => API.post(`/page/callRecord`, params);

// ---------------- Counterparty Check ------------------------
export const checkCounterparty = (counterpartyName, token) =>
  API.get(`/kyc/counterparty?counterpartyName=${encodeURIComponent(counterpartyName)}`, {
    headers: { Authorization: `Bearer ${getAccessToken()}` },
  });
